import React from 'react';
import { useParams, Link } from 'react-router-dom'; // Import Link
import { useTranslation } from 'react-i18next';
import { vehicles } from '../data/vehicles';
import VehicleCard from '../components/VehicleCard';
import { SEOHead } from '../components/SEOHead';
import { Locale } from '../i18n/seoConfig'; // Import Locale type
import { JsonLd } from 'react-schemaorg'; // Import JsonLd
import { ItemList, ListItem } from 'schema-dts'; // Import Schema types

export const VehicleTypePage: React.FC = () => {
  const { type } = useParams<{ type: string }>();
  const { t, i18n } = useTranslation();
  const locale = i18n.language as Locale;

  const normalizedType = type?.toLowerCase() || '';
  const typeDisplayName = t(`types.${normalizedType}`, normalizedType.charAt(0).toUpperCase() + normalizedType.slice(1)); // Get translated type name

  const vehiclesOfType = vehicles.filter(v =>
    v.type.toLowerCase() === normalizedType
  );

  // SEO Content
  const title = t('vehicleTypePage.title', { type: typeDisplayName });
  const description = t('vehicleTypePage.description', { type: typeDisplayName, count: vehiclesOfType.length });
  const canonicalPath = `/vehicles/type/${normalizedType}`;
  const breadcrumbs = [
    { label: t('breadcrumbs.home'), path: `/${locale}` },
    { label: t('breadcrumbs.vehicles'), path: `/${locale}/vehicles` },
    { label: typeDisplayName, path: `/${locale}${canonicalPath}` }
  ];

  // ItemList Schema
  const itemListSchema: ItemList = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": title,
    "description": description,
    "numberOfItems": vehiclesOfType.length,
    "itemListElement": vehiclesOfType.map((vehicle, index): ListItem => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product", // Use Product or Car
        "name": `${vehicle.brand} ${vehicle.name}`,
        "url": `https://rodfaifa.com/${locale}/vehicles/${vehicle.id}`,
        "image": vehicle.image,
        "brand": {
          "@type": "Brand",
          "name": vehicle.brand
        },
        "offers": {
          "@type": "Offer",
          "price": vehicle.price.toString(),
          "priceCurrency": "THB",
          "availability": "https://schema.org/InStock"
        }
      }
    }))
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <SEOHead
        title={title}
        description={description}
        canonicalPath={canonicalPath} // Pass path without locale
        locale={locale}
        breadcrumbs={breadcrumbs}
        schema={itemListSchema} // Add ItemList schema
      />
      {/* Remove separate JsonLd if SEOHead handles it */}

      <h1 className="text-3xl md:text-4xl font-bold mb-6">
        {t('vehicleTypePage.heading', { type: typeDisplayName })}
      </h1>
      <p className="text-gray-600 mb-8">{t('vehicleTypePage.subheading', { count: vehiclesOfType.length })}</p>

      {vehiclesOfType.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
          {vehiclesOfType.map(vehicle => (
            // Pass handleCompare and isCompared if comparison is enabled here
            <VehicleCard key={vehicle.id} vehicle={vehicle} />
          ))}
        </div>
      ) : (
        <p>{t('vehicleTypePage.noVehiclesFound')}</p>
      )}
    </div>
  );
};

export default VehicleTypePage;